import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';

function formatError(detail) {
  if (!detail) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div style={{ maxWidth: 480, margin: '80px auto', padding: '0 24px', textAlign: 'center' }} data-testid="reset-invalid">
        <div style={{ fontSize: 32, marginBottom: 12 }}>✦</div>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 300, marginBottom: 10 }}>This reset link is invalid</div>
        <p style={{ color: 'var(--muted)', marginBottom: 24, fontSize: 14, lineHeight: 1.7 }}>
          Please request a new password reset link from the sign-in screen.
        </p>
        <button className="tool-btn" onClick={() => navigate('/')} data-testid="reset-go-home">Go home</button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 460, margin: '80px auto', padding: '0 24px' }} data-testid="reset-password-page">
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 32, fontWeight: 300, color: 'var(--deep)' }}>
          <em>Little</em> Blue Numbers
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted)', letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: 4 }}>Reset your password</div>
      </div>

      <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 16, padding: 28, boxShadow: '0 2px 12px rgba(0,0,0,0.03)' }}>
        {!done ? (
          <form onSubmit={handleSubmit}>
            <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.7, marginBottom: 20, marginTop: 0 }}>
              Choose a new password. Make it at least 6 characters.
            </p>

            {error && <div className="auth-error" data-testid="reset-error">{error}</div>}

            <label className="field-label">New password</label>
            <input
              className="field-input"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              required
              autoComplete="new-password"
              data-testid="reset-password-input"
            />

            <label className="field-label">Confirm new password</label>
            <input
              className="field-input"
              type="password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="Type it again"
              required
              autoComplete="new-password"
              data-testid="reset-password-confirm"
            />

            <button type="submit" className="auth-submit" disabled={loading} style={{ marginTop: 8 }} data-testid="reset-submit">
              {loading ? 'Resetting...' : 'Set new password'}
            </button>
          </form>
        ) : (
          <div style={{ textAlign: 'center', padding: '16px 0' }} data-testid="reset-success">
            <div style={{ fontSize: 36, marginBottom: 12 }}>✦</div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, fontWeight: 300, marginBottom: 10 }}>Password reset</div>
            <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.7, marginBottom: 24 }}>
              Your password has been updated. You can now sign in with your new password.
            </p>
            <button
              className="auth-submit"
              onClick={() => navigate('/')}
              data-testid="reset-go-signin"
            >
              Continue to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
